import type { LucideIcon } from 'lucide-react';
import { Pencil, Save, X, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

interface EditableCardProps {
  title: string;
  icon: LucideIcon;
  canEdit: boolean;
  isEditing: boolean;
  onEditStart: () => void;
  onSave: () => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
  error?: string | null;
  children: React.ReactNode;
  className?: string;
  /** Aggancio per i test E2E: identifica la card e i suoi controlli. */
  testid?: string;
}

export default function EditableCard({
  title,
  icon: Icon,
  canEdit,
  isEditing,
  onEditStart,
  onSave,
  onCancel,
  isSaving,
  error,
  children,
  className,
  testid,
}: EditableCardProps) {
  const { t } = useTranslation();
  return (
    <div className={cn('bg-white border border-stone-mid rounded-xl overflow-hidden', className)} data-testid={testid}>
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-stone-mid bg-stone-bg">
        <Icon size={16} className="text-sage flex-shrink-0" />
        <h3 className="text-sm font-semibold text-ink-dark flex-1">{title}</h3>

        {canEdit && !isEditing && (
          <button
            type="button"
            onClick={onEditStart}
            data-testid={testid ? `${testid}-modifica` : undefined}
            className="flex items-center gap-1.5 text-xs text-sage hover:text-ink-dark transition-colors px-2 py-1 rounded hover:bg-stone-mid"
          >
            <Pencil size={12} />
            {t('common.azioni.modifica')}
          </button>
        )}

        {isEditing && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSaving}
              className="flex items-center gap-1 text-xs text-ink-soft hover:text-ink-dark transition-colors px-2 py-1 rounded hover:bg-stone-mid disabled:opacity-50"
            >
              <X size={12} />
              {t('common.azioni.annulla')}
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={isSaving}
              data-testid={testid ? `${testid}-salva` : undefined}
              className="flex items-center gap-1 text-xs font-medium text-white bg-sage hover:bg-[#5C6F59] disabled:opacity-50 disabled:cursor-not-allowed transition-colors px-3 py-1 rounded"
            >
              {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              {isSaving ? t('common.azioni.salvataggio_in_corso') : t('common.azioni.salva')}
            </button>
          </div>
        )}
      </div>

      <div className="px-5 py-4">{children}</div>

      {isEditing && error && (
        <div className="px-5 pb-3 text-xs text-accent-danger">{error}</div>
      )}
    </div>
  );
}
